import React, { useState, useEffect, useCallback } from 'react'
import { shallow } from 'zustand/shallow'

// Material Dashboard 2 React components
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import { DataGrid } from '@mui/x-data-grid'

import CustomToolbar from './CustomToolbar/CustomToolbar'
import CustomFooter from './CustomFooter/CustomFooter'
import getTransactionsDataModel from './transactionsDataModel'
import { txType } from '../../types'
import { useAirdropState } from '../../../../contexts/airDropContext'
import { validateTransactions } from '../../actions/blockchainActions'
import { chainsDataType, networkType } from '../../../../util/chainsData'
import { useRpcCountStore } from 'src/store/rpcCount'
import './TransactionsTable.scss'

declare module '@mui/x-data-grid' {
  interface FooterPropsOverrides {
    toggleExecuteModal: () => void
    toggleRemoveModal: () => void
    selectedTransactions: any[]
    onCheckTransactions: () => void
    isMobile: boolean
    disabled: boolean
    checkableTransactions: txType[]
    executableTransactions: txType[]
  }
}

const TransactionsTable = ({
  transactionsData,
  handleSelectionChange,
  handleTransactionClick,
  toggleExecuteModal,
  toggleRemoveModal,
  selectedTransactions,
  chain,
  network,
}: {
  transactionsData: txType[]
  handleSelectionChange: (params: any) => void
  handleTransactionClick: (params: any) => void
  toggleExecuteModal: () => void
  toggleRemoveModal: () => void
  selectedTransactions: any[]
  chain: keyof chainsDataType
  network: networkType
}) => {
  const [tableHeight, setTableHeight] = useState(300)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(
    parseInt(sessionStorage.getItem('transactionsTablePageSize') ?? '50'),
  )
  const [isMobile, setIsMobile] = useState(false)

  const airdropState = useAirdropState()
  const { rpcCount, incrementRpcCount } = useRpcCountStore(
    (state) => ({ rpcCount: state.count, incrementRpcCount: state.increment }),
    shallow,
  )

  const muiDataModel = getTransactionsDataModel(transactionsData, chain, network)

  const checkableTransactions = transactionsData?.filter(
    (tx) => (tx.status === 'pending' || tx.status === 'failed') && tx.tx,
  )

  const executableTransactions = transactionsData?.filter(
    (tx) => selectedTransactions.includes(tx.id) && tx.status === 'new',
  )

  const calculateTableHeight = useCallback(() => {
    const rowsNumber =
      transactionsData?.length - page * pageSize < pageSize ? transactionsData?.length : pageSize

    let newHeight = transactionsData?.length
      ? (rowsNumber + 2) * 54 +
        (window.visualViewport && window.visualViewport.width <= 480
          ? 190
          : window.visualViewport && window.visualViewport.width < 853
          ? 160
          : 100)
      : 300

    if (isMobile) {
      newHeight += 430
    }

    return newHeight < 300 ? 300 : newHeight > 800 ? 800 : newHeight
  }, [transactionsData?.length, page, pageSize, isMobile])

  function savePageSize() {
    sessionStorage.setItem('transactionsTablePageSize', pageSize.toString())
  }

  useEffect(() => {
    savePageSize()
  }, [pageSize])

  useEffect(() => {
    if (window.innerWidth < 590) {
      setIsMobile(true)
    }
  }, [airdropState.initiator])

  useEffect(() => {
    setTableHeight(calculateTableHeight())
  }, [page, transactionsData?.length, isMobile])

  return (
    <Paper
      elevation={2}
      sx={{
        background: '#181D38',
        color: '#fff',
        padding: '12px 18px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '15px',
        width: '100%',
        boxSizing: 'border-box',
        borderRadius: '8px',
      }}>
      <Box pt={3} sx={{ width: '100%' }}>
        <div
          style={{
            height: `${tableHeight}px`,
            width: '100%',
          }}>
          {transactionsData ? (
            <DataGrid
              {...muiDataModel}
              disableColumnMenu
              slots={{ toolbar: CustomToolbar, footer: CustomFooter }}
              slotProps={{
                toolbar: {
                  isMobile,
                },
                footer: {
                  // handleAddressChange,
                  // handleAddressPressEnter,
                  toggleExecuteModal,
                  toggleRemoveModal,
                  selectedTransactions,
                  checkableTransactions,
                  executableTransactions,
                  onCheckTransactions: () =>
                    validateTransactions(
                      checkableTransactions,
                      chain,
                      network,
                      rpcCount,
                      incrementRpcCount,
                    ),
                  isMobile,
                },
              }}
              // pageSize={pageSize}
              paginationModel={{
                page,
                pageSize,
              }}
              pageSizeOptions={[25, 50, 100]}
              // onPageSizeChange={(newPageSize: number) =>
              //   setPageSize(newPageSize)
              // }
              onPaginationModelChange={(model) => {
                setPageSize(model.pageSize)
                setPage(model.page)
              }}
              // onPageChange={(page: number) => setPage(page)}
              pagination
              onRowClick={handleTransactionClick}
              checkboxSelection
              disableRowSelectionOnClick
              // onSelectionModelChange={handleSelectionChange}
              onRowSelectionModelChange={handleSelectionChange}
              // selectionModel={selectedTransactions}
              rowSelectionModel={selectedTransactions}
              autoHeight={false}
              sx={{
                border: 'none',
                padding: '0 10px',
                color: 'white',
                '& .MuiDataGrid-cell': {
                  border: 'none !important',
                },
                '& .MuiDataGrid-columnHeader': {
                  outline: 'none !important',
                  fontFamily: 'SpaceGrotesk, sans-serif',
                },
                '& .MuiDataGrid-overlay': {
                  backgroundColor: 'transparent',
                },
                '.MuiTablePagination-root': {
                  color: 'white',
                },
                '.MuiDataGrid-row': { cursor: 'pointer' },
                '& .MuiCheckbox-root': {
                  color: 'white',
                },
                '& .MuiTablePagination-selectLabel': {
                  fontFamily: 'SpaceGrotesk, sans-serif',
                },
                '& .MuiTablePagination-select': {
                  fontFamily: 'SpaceGrotesk, sans-serif',
                },
                '& .MuiInputBase-root svg.MuiSvgIcon-root': {
                  color: 'white',
                },
                '& .MuiTablePagination-displayedRows': {
                  fontFamily: 'SpaceGrotesk, sans-serif',
                },
                '& .MuiIconButton-root': {
                  color: 'white',
                },
                '& .MuiDataGrid-columnHeaders:hover .MuiDataGrid-columnSeparator': {
                  display: 'none',
                },
                '& .MuiDataGrid-iconButtonContainer': {
                  visibility: 'visible',
                },
                '& .MuiDataGrid-sortIcon.MuiSvgIcon-root': {
                  opacity: '0.5 !important',
                },
              }}
              // error={isError}
            />
          ) : null}
        </div>
      </Box>
    </Paper>
  )
}

export default TransactionsTable
